import { DetailField, DetailGrid, DetailSection } from "@/components/show";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TeamLink } from "@/pages/sports/components/team-link";
import { fmtCount, fmtFraction, fmtNumber } from "@/pages/sports/lib/format";
import type { ILeaderRow, ILeagueStats } from "@/types/league-standings";

/** Season-wide figures over finished league matches. Percentages arrive as 0..1 fractions. */
export function LeagueStatsCard({ stats }: { stats: ILeagueStats }) {
  return (
    <DetailSection title="League stats">
      <DetailGrid className="sm:grid-cols-3">
        <DetailField
          label="Matches played"
          value={fmtCount(stats.matchesPlayed)}
        />
        {stats.goalsPerMatch != null && (
          <DetailField
            label="Goals per match"
            value={fmtNumber(stats.goalsPerMatch)}
          />
        )}
        <DetailField label="Home wins" value={fmtFraction(stats.homeWinPct)} />
        {stats.drawPct != null && (
          <DetailField label="Draws" value={fmtFraction(stats.drawPct)} />
        )}
        <DetailField label="Away wins" value={fmtFraction(stats.awayWinPct)} />
      </DetailGrid>
    </DetailSection>
  );
}

/** Top scorers or top assists: up to ten rows, rank shared on ties. */
export function LeadersCard({
  title,
  valueLabel,
  rows,
}: {
  title: string;
  valueLabel: string;
  rows: ILeaderRow[];
}) {
  return (
    <DetailSection title={title}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">#</TableHead>
            <TableHead>Player</TableHead>
            <TableHead>Team</TableHead>
            <TableHead className="text-right">{valueLabel}</TableHead>
            <TableHead className="text-right" title="Games played">
              GP
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row.player?.providerId ?? i}>
              <TableCell className="tabular-nums text-muted-foreground">
                {fmtCount(row.rank)}
              </TableCell>
              <TableCell>{row.player?.name ?? "—"}</TableCell>
              <TableCell>
                <TeamLink team={row.team} />
              </TableCell>
              <TableCell className="text-right font-semibold tabular-nums">
                {fmtCount(row.value)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtCount(row.gamesPlayed)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DetailSection>
  );
}
